import { type MouseEvent, useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import { handleScrollIntoViewById } from "@/shared/helpers/handleScrollIntoViewById";
import { StockScannerEvent } from "../../../types";
import { StripePricingTableDialog } from "../StripePricingTableDialog";

export const MarketsHeader = ({
	eventEmitter,
}: {
	eventEmitter?: EventEmitter;
}) => {
	const { login } = useAuth();
	const [isDialogOpen, setIsDialogOpen] = useState(false);

	const handleLoginButtonClick = useCallback(
		(e: MouseEvent) => {
			e.preventDefault();
			login();
		},
		[login],
	);

	const handlePricingButtonClick = useCallback((e: MouseEvent) => {
		e.preventDefault();
		setIsDialogOpen(true);
	}, []);

	const handleMacroClick = useCallback(
		() => handleScrollIntoViewById("macro"),
		[],
	);

	const handleSectorsClick = useCallback(
		() => handleScrollIntoViewById("sectors"),
		[],
	);

	const handleStockScannersClick = useCallback(
		() => handleScrollIntoViewById("stock-scanners"),
		[],
	);

	const handleEarningsMomentumClick = useCallback(() => {
		eventEmitter?.emit("stockScannerNavigation", {
			section: "earnings-momentum",
			eventValue: StockScannerEvent.EARNINGS_MOMENTUM,
		});

		handleScrollIntoViewById("timing-momentum");
	}, [eventEmitter]);

	const handleTimingMomentumClick = useCallback(() => {
		eventEmitter?.emit("stockScannerNavigation", {
			section: "timing-momentum",
			eventValue: StockScannerEvent.TIMING_MOMENTUM,
		});

		handleScrollIntoViewById("timing-momentum");
	}, [eventEmitter]);

	const handleStockDriftClick = useCallback(() => {
		eventEmitter?.emit("stockScannerNavigation", {
			section: "stock-drift",
			eventValue: StockScannerEvent.UPDRIFT,
		});

		handleScrollIntoViewById("stock-drift");
	}, [eventEmitter]);

	const handlePowerdaysClick = useCallback(() => {
		eventEmitter?.emit("stockScannerNavigation", {
			section: "powerdays",
			eventValue: StockScannerEvent.POWERDAYS,
		});

		handleScrollIntoViewById("stock-drift");
	}, [eventEmitter]);

	const handleEarningsClick = useCallback(
		() => handleScrollIntoViewById("earnings"),
		[],
	);

	return (
		<div
			className="flex flex-col gap-3 p-2 @container"
			data-testid="markets-header"
		>
			<div className="flex [@container(max-width:700px)]:justify-center justify-between items-center gap-4 flex-wrap">
				<img
					src="/eps-logo.webp"
					alt="EPSMomentum Logo"
					width={216}
					height="auto"
					loading="eager"
				/>
				<nav
					className="flex flex-wrap justify-between gap-x-5 text-lg"
					aria-label="Markets Navigation"
				>
					<Button
						variant="link"
						onClick={handleLoginButtonClick}
						className="no-underline text-[var(--light-gray)] hover:text-accent-foreground"
					>
						Become Member
					</Button>
					<a
						href="https://pages.epsmomentum.com/webinars"
						target="_blank"
						rel="noopener noreferrer"
						className="no-underline text-[var(--light-gray)] hover:text-accent-foreground"
					>
						Webinars
					</a>
					<a
						href="https://epsmomentum.com/tutorials"
						target="_blank"
						rel="noopener noreferrer"
						className="no-underline text-[var(--light-gray)] hover:text-accent-foreground"
					>
						Knowledge Lab
					</a>
					<Button
						variant="link"
						onClick={handlePricingButtonClick}
						className="no-underline text-[var(--light-gray)] hover:text-accent-foreground"
					>
						Pricing
					</Button>
					<a
						href="https://epsmomentum.com"
						target="_blank"
						rel="noopener noreferrer"
						className="no-underline text-[var(--light-gray)] hover:text-accent-foreground"
					>
						About
					</a>
				</nav>
			</div>

			<nav
				className="flex flex-wrap justify-center gap-x-5 text-lg"
				aria-label="Markets Sections Navigation"
			>
				<Button
					variant="link"
					onClick={handleMacroClick}
					className="no-underline font-bold text-accent-foreground"
				>
					Macro
				</Button>
				<Button
					variant="link"
					onClick={handleSectorsClick}
					className="no-underline font-bold text-accent-foreground"
				>
					Sectors
				</Button>
				<Button
					variant="link"
					onClick={handleStockScannersClick}
					className="no-underline font-bold text-accent-foreground"
				>
					Stock Scanners
				</Button>
				<Button
					variant="link"
					onClick={handleEarningsMomentumClick}
					className="no-underline font-bold text-accent-foreground"
				>
					Earnings Momentum
				</Button>
				<Button
					variant="link"
					onClick={handleTimingMomentumClick}
					className="no-underline font-bold text-accent-foreground"
				>
					Timing Momentum
				</Button>
				<Button
					variant="link"
					onClick={handleStockDriftClick}
					className="no-underline font-bold text-accent-foreground"
				>
					Stock Drift
				</Button>
				<Button
					variant="link"
					onClick={handlePowerdaysClick}
					className="no-underline font-bold text-accent-foreground"
				>
					Powerdays
				</Button>
				<Button
					variant="link"
					onClick={handleEarningsClick}
					className="no-underline font-bold text-accent-foreground"
				>
					Earnings
				</Button>
			</nav>

			{isDialogOpen && (
				<StripePricingTableDialog
					open={isDialogOpen}
					onOpenChange={setIsDialogOpen}
				/>
			)}
		</div>
	);
};
